// OpsAudit header rows inline in the table, collapsible, with a "No Audit"
// section for ungrouped work steps.
import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FolderOpen } from "lucide-react";
import {
  TableBody,
  TableCell,
  TableRow,
} from "../../../../components/ui/table";
import {
  OpsAudit,
  OptroWorkStepSummary,
  WorkStepWithUser,
} from "../../../../models/audit/workStep";
import { groupKey, groupWorkSteps } from "../lib/groupWorkSteps";
import { WorkStepsTableHeader } from "./WorkStepsTableHeader";
import { WorkStepTableRow } from "./WorkStepTableRow";
import { ImportingWorkStepRow } from "./ImportingWorkStepRow";
import { WorkStepsEmptyRow } from "./WorkStepsEmptyRow";
import { WorkStepsWelcomeState } from "./WorkStepsWelcomeState";

interface WorkStepsGroupedTableProps {
  workSteps: WorkStepWithUser[];
  opsAudits: OpsAudit[];
  // Optro work steps still importing — placeholder rows until each lands.
  importingWorkSteps: OptroWorkStepSummary[];
  selectedWorkStep: WorkStepWithUser | null;
  onWorkStepClick: (workStep: WorkStepWithUser) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort?: (field: string) => void;
  // Infinite-scroll observer target, attached to the last *visible* work step
  // across all (non-collapsed) groups.
  lastWorkStepRef?: (node: HTMLTableRowElement | null) => void;
  hasActiveFilters: boolean;
  loading?: boolean;
}

export const WorkStepsGroupedTable = ({
  workSteps,
  opsAudits,
  importingWorkSteps,
  selectedWorkStep,
  onWorkStepClick,
  sortBy,
  sortOrder,
  onSort,
  lastWorkStepRef,
  hasActiveFilters,
  loading = false,
}: WorkStepsGroupedTableProps) => {
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(
    new Set(),
  );

  // Grouping walks every audit against every work step, so it stays out of the
  // render path: the import-progress context re-renders this page on a 2s poll.
  const groups = useMemo(
    () => groupWorkSteps(workSteps, opsAudits, importingWorkSteps),
    [workSteps, opsAudits, importingWorkSteps],
  );

  // With no work steps at all (and nothing filtered away), skip the table
  // chrome entirely and show the welcome entry cards on their own — mirrors
  // ControlsTable :163-165. A first import in flight shows the table with
  // its placeholder rows instead.
  if (
    !loading &&
    workSteps.length === 0 &&
    importingWorkSteps.length === 0 &&
    !hasActiveFilters
  ) {
    return <WorkStepsWelcomeState />;
  }

  // Determine the last *visible* (i.e. not inside a collapsed group) work
  // step across all groups, for the infinite-scroll observer ref.
  let lastVisibleWorkStepId: string | null = null;
  groups.forEach((group) => {
    const key = groupKey(group);
    if (collapsedGroups.has(key)) return;
    if (group.workSteps.length > 0) {
      lastVisibleWorkStepId = group.workSteps[group.workSteps.length - 1].id;
    }
  });

  const toggleGroup = (key: string) => {
    setCollapsedGroups((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <table className="w-full table-fixed caption-bottom text-sm border-separate border-spacing-0">
      <WorkStepsTableHeader
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={onSort}
      />
      <TableBody>
        {groups.length === 0 ? (
          <WorkStepsEmptyRow colSpan={5} />
        ) : (
          groups.map((group) => {
            const key = groupKey(group);
            const isCollapsed = collapsedGroups.has(key);
            const configuredCount = group.workSteps.filter(
              (workStep) => workStep.configuredState === "configured",
            ).length;
            return [
              <TableRow
                key={`header-${key}`}
                role="button"
                className="cursor-pointer bg-stone-100/80 hover:bg-stone-200/60 transition-colors"
                onClick={() => toggleGroup(key)}
              >
                <TableCell colSpan={5} className="border-x py-2.5">
                  <div className="flex items-center gap-2">
                    {isCollapsed ? (
                      <ChevronRight className="h-4 w-4 text-stone-500" />
                    ) : (
                      <ChevronDown className="h-4 w-4 text-stone-500" />
                    )}
                    <FolderOpen className="h-4 w-4 text-stone-500" />
                    <span className="font-semibold text-stone-800">
                      {group.opsAudit?.name ?? "No Audit"}
                    </span>
                    {group.opsAudit?.externalRef && (
                      <span className="rounded bg-white px-1.5 py-0.5 text-[10px] font-medium text-stone-500 border border-stone-200">
                        {group.opsAudit.externalRef}
                      </span>
                    )}
                    <span className="ml-auto text-xs text-stone-500">
                      {group.workSteps.length > 0 &&
                        `${configuredCount}/${group.workSteps.length} configured`}
                      {group.workSteps.length > 0 &&
                        group.importingWorkSteps.length > 0 &&
                        " · "}
                      {group.importingWorkSteps.length > 0 &&
                        `${group.importingWorkSteps.length} importing`}
                    </span>
                  </div>
                </TableCell>
              </TableRow>,
              // Placeholders lead their group: under the default newest-first
              // sort, each lands roughly where its placeholder sat.
              ...(isCollapsed
                ? []
                : group.importingWorkSteps.map((importing) => (
                    <ImportingWorkStepRow
                      key={`importing-${importing.opsAuditItemId}`}
                      workStep={importing}
                    />
                  ))),
              ...(isCollapsed
                ? []
                : group.workSteps.map((workStep) => (
                    <WorkStepTableRow
                      key={workStep.id}
                      workStep={workStep}
                      isSelected={selectedWorkStep?.id === workStep.id}
                      onClick={onWorkStepClick}
                      rowRef={
                        workStep.id === lastVisibleWorkStepId
                          ? lastWorkStepRef
                          : undefined
                      }
                    />
                  ))),
            ];
          })
        )}
      </TableBody>
    </table>
  );
};
